import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { setDraggedFile, showCreateSiteModal } from '../Manage/actions'
import { useLocation } from 'react-router-dom'

/**
 * Global file receiver - works on any route
 * Functional component version with hooks
 */
const GlobalTiinyReceiver = () => {
  const dispatch = useDispatch()
  const senderOriginRef = useRef(null)
  const messageListenerRef = useRef(null)
  const location = useLocation()

  const convertToFileObjects = async (receivedFiles) => {
    const fileObjects = []

    try {
      for (const receivedFile of receivedFiles) {
        let blob

        if (receivedFile.encoding === 'base64') {
          const base64Data = receivedFile.content.split(',')[1]
          const byteCharacters = atob(base64Data)
          const byteArray = new Uint8Array(byteCharacters.length)

          for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i)
          }

          blob = new Blob([byteArray], { type: receivedFile.type })
        } else {
          // Handle text files
          blob = new Blob([receivedFile.content], { type: receivedFile.type })
        }

        // Create File object
        const file = new File([blob], receivedFile.name, {
          type: receivedFile.type,
          lastModified: Date.now()
        })

        // Add source metadata
        Object.defineProperty(file, 'source', {
          value: receivedFile.source,
          writable: false
        })

        fileObjects.push(file)
      }
    } catch (error) {
      throw new Error(`Failed to convert files: ${error.message}`)
    }

    return fileObjects
  }

  // Send message back to sender
  const sendMessage = (data) => {
    const targetWindow = window.opener || window.parent
    if (targetWindow && targetWindow !== window && senderOriginRef.current) {
      try {
        targetWindow.postMessage(data, senderOriginRef.current)
      } catch (error) {
        console.error(`Failed to send message: ${error.message}`)
      }
    }
  }

  const processFiles = async (files) => {
    try {
      const fileObjects = await convertToFileObjects(files)

      if (fileObjects.length === 0) {
        throw new Error('No valid files processed')
      }
      // Set in Redux store using your existing action
      const result = fileObjects.length === 1 ? fileObjects[0] : fileObjects
      dispatch(setDraggedFile(result))

      if (location.pathname.includes('/manage')) {
        dispatch(showCreateSiteModal(true))
      }

      return {
        fileCount: fileObjects.length,
        processedAt: new Date().toISOString()
      }
    } catch (error) {
      throw error
    }
  }

  // Main message handler
  const handleMessage = async (event) => {
    // Store sender origin
    if (!senderOriginRef.current) {
      senderOriginRef.current = event.origin
    }

    const { type, files, metadata } = event.data

    if (type === 'TIINY_FILE_PRELOAD') {
      try {
        // Process files
        const result = await processFiles(files || [])

        // Send success response
        sendMessage({
          type: 'TIINY_UPLOAD_SUCCESS',
          data: {
            shareableLink: 'Files integrated successfully',
            deploymentId: `proc_${Date.now()}`,
            ...result
          }
        })
      } catch (error) {
        // Send error response
        sendMessage({
          type: 'TIINY_UPLOAD_ERROR',
          data: {
            message: error.message,
            code: 'PROCESSING_FAILED'
          }
        })
      }
    }
  }

  useEffect(() => {
    // Setup message listener
    messageListenerRef.current = handleMessage
    window.addEventListener('message', handleMessage)

    // Send ready signal
    const sendReady = () => {
      const targetWindow = window.opener || window.parent
      if (targetWindow && targetWindow !== window) {
        targetWindow.postMessage({ type: 'TIINY_READY' }, '*')
      }
    }

    const readyTimer = setTimeout(sendReady, 100)

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage)
      clearTimeout(readyTimer)
    }
  }, [])

  return null
}

export default GlobalTiinyReceiver
